"use client";

import Image from "next/image";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

interface Testimonial {
  text: string;
  authorName: string;
  picture: {
    data: {
      id: string;
      attributes: {
        name: string;
        alternativeText: string;
        url: string;
      };
    };
  };
  rating: number;
}

interface TestimonialsProps {
  data: {
    id: string;
    title: string;
    description: string;
    testimonials: Testimonial[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}



function TestimonialCard({ text, authorName, picture, rating }: Testimonial) {
  const imageUrl = getStrapiMedia(picture?.data?.attributes?.url);

  return (
    <div className="relative w-full h-[350px] overflow-hidden shadow-md flex flex-col">
      {/* Background image */}
      {imageUrl && (
        <div>

          <Image
            src={imageUrl}
            alt={picture?.data?.attributes?.alternativeText || "Hotel"}
            fill
            className="object-cover"
          />
          <div className="absolute inset-2 pointer-events-none ring-2 ring-[--white] ring-inset z-10" />

        </div>
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

      {/* Content */}
      <div className="relative z-20 flex flex-col justify-end items-center text-center h-full p-6 text-white gap-3">
        {/* Name */}
        <div className="text-lg font-semibold">{authorName}</div>

        {/* Stars */}
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <svg
              key={i}
              className={`w-5 h-5 ${i < rating ? 'text-[#FFC107]' : 'text-gray-400'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>

        {/* Text */}
        <p className="italic text-sm leading-relaxed max-w-md line-clamp-4 font-times">
          "{text}"
        </p>
      </div>

    </div>
  );
}


export default function Testimonials({ data }: TestimonialsProps) {
  if (!data.isLive) return null;

  return (
    <section className="px-4 py-16 md:py-24 bg-[#e9e9e9]">
      <div className="mx-auto max-w-screen-xl">
        {data.title && (
          <div className="text-center mb-12 px-6">
            {data.useH1Tag ? (
              <h1 className="text-3xl md:text-4xl font-semibold text-th-secondary font-cinzel tracking-wider uppercase">{data.title}</h1>
            ) : (
              <h2 className="text-3xl md:text-4xl font-semibold text-th-secondary">{data.title}</h2>
            )}
            {data.description && (
              <p className="mt-4 text-xl md:text-2xl text-gray-700 font-light font-times">{data.description}</p>
            )}
          </div>
        )}

        <div className="relative">
          <Swiper
            slidesPerView={1}
            spaceBetween={30}
            speed={900}
            loop={true}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            navigation={{ nextEl: ".testimonial-next", prevEl: ".testimonial-prev" }}
            breakpoints={{
              1024: { slidesPerView: 3 },
            }}
            modules={[Navigation, Autoplay]}
            className="testimonial-swiper"
          >
            {data.testimonials.map((testimonial, index) => (
              <SwiperSlide key={index} className="pb-8">
                <TestimonialCard {...testimonial} />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="flex justify-center gap-4">
            <button
              className="testimonial-prev p-3 text-black transition"
              aria-label="Previous"
            >
              <FaChevronLeft size={20} />
            </button>
            <button
              className="testimonial-next p-3  text-black transition"
              aria-label="Next"
            >
              <FaChevronRight size={20} />
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}
